import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/usePageTitle";
import EmailComposer from "./EmailComposer";
import { tradeLabel } from "@/components/TradeCategoryPicker";

const STATUS_CLS = { requested: "requested", quoted: "quote", matched: "matched", scheduled: "matched", in_progress: "matched", completed: "done", cancelled: "requested" };

export default function AdminDashboard() {
  usePageTitle("Command centre — Olivia Does");
  const [tab, setTab] = useState("action");
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [ops, setOps] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [runningNudge, setRunningNudge] = useState(false);
  const [partners, setPartners] = useState([]);

  const loadNudge = () => api.get("/admin/rebooking-nudges/pending").then((r) => setNudge(r.data)).catch(() => {});
  const loadPartners = () => api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/action-needed").then((r) => setOps(r.data)).catch(() => {});
    loadNudge();
    loadPartners();
  }, []);

  const reopenJob = async (j) => {
    if (!window.confirm(`Reopen "${j.title}"? It will move back to active so issues can be resolved.`)) return;
    try {
      await api.post(`/jobs/${j.id}/reopen`);
      toast.success("Job reopened.");
      api.get("/jobs").then((r) => setJobs(r.data));
      api.get("/admin/action-needed").then((r) => setOps(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reopen job"); }
  };

  const deleteJob = async (j) => {
    if (!window.confirm(`Permanently delete "${j.title}"? This removes the job and its quotes, payments, offers and reviews. This cannot be undone.`)) return;
    try {
      await api.delete(`/jobs/${j.id}`);
      toast.success("Job deleted.");
      api.get("/jobs").then((r) => setJobs(r.data));
      api.get("/admin/action-needed").then((r) => setOps(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete job"); }
  };

  const reviewDoc = async (docId, action) => {
    let note = null;
    if (action === "reject") {
      note = window.prompt("Reason for rejection (the partner will see this):", "");
      if (note === null) return;
    }
    try {
      await api.post(`/admin/documents/${docId}/review`, { action, note });
      toast.success(action === "approve" ? "Document approved." : "Document rejected — partner notified.");
      loadPartners();
    } catch (e) { toast.error("Could not update document"); }
  };

  const runNudges = async () => {
    setRunningNudge(true);
    try {
      const { data } = await api.post("/admin/rebooking-nudges/run");
      toast.success(data.sent ? `Sent ${data.sent} check-up ${data.sent === 1 ? "email" : "emails"}.` : "No customers are due a nudge right now.");
      loadNudge();
    } catch (e) { toast.error("Could not run nudges"); }
    finally { setRunningNudge(false); }
  };

  const vetQueue = partners.filter((p) => p.state === "under_review" || p.state === "rejected").length;
  const actionTotal = ops ? Object.values(ops.counts).reduce((a, b) => a + b, 0) : 0;
  const TABS = [["action", `Action needed${actionTotal ? ` (${actionTotal})` : ""}`], ["jobs", `All jobs (${jobs.length})`], ["users", `People (${users.length})`], ["vetting", `Vetting${vetQueue ? ` (${vetQueue})` : ""}`], ["retention", "Retention"], ["email", "Email"]];

  const statCards = stats ? [
    ["Open jobs", stats.open_jobs], ["Scheduled", stats.scheduled], ["In progress", stats.in_progress],
    ["Completed", stats.completed], ["Customers", stats.customers], ["Trades", stats.trades],
    ["Staff", stats.staff], ["Revenue", `£\u2009${stats.revenue_completed || 0}`],
  ] : [];

  return (
    <section className="od-section od-wrap" data-testid="admin-dashboard" style={{ maxWidth: 1280 }}>
      <div className="od-eyebrow">Olivia · Operations</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, margin: "6px 0 24px" }}>
        <h1 className="serif" style={{ fontSize: "2.1rem", fontWeight: 500, margin: 0 }}>Command centre</h1>
        <Link to="/admin/cms" data-testid="cms-link" className="btn secondary sm">Open content studio →</Link>
      </div>

      <div className="ogrid cols-3" style={{ gridTemplateColumns: "repeat(4,1fr)", marginBottom: 30 }}>
        {statCards.map(([l, v]) => (
          <div className="statcard" key={l} data-testid={`stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <b style={{ fontSize: "1.7rem" }}>{v}</b><span>{l}</span>
          </div>
        ))}
      </div>

      <div className="pillrow" style={{ marginBottom: 24 }}>
        {TABS.map(([v, label]) => (
          <div key={v} data-testid={`admin-tab-${v}`} className={`pill ${tab === v ? "on" : ""}`} onClick={() => setTab(v)}>{label}</div>
        ))}
      </div>

      {/* ACTION NEEDED */}
      {tab === "action" && ops && (
        <div data-testid="admin-action-needed">
          {actionTotal === 0 && <div className="card" style={{ textAlign: "center", color: "var(--taupe)", padding: 40 }}>All clear — nothing needs your attention right now. ✨</div>}
          <OpsGroup title="⚠ Stale — unassigned over 2h" items={ops.stale_offers} testid="ops-stale" render={(j) => <JobRow j={j} />} />
          <OpsGroup title="New bookings awaiting a partner" items={ops.new_bookings} testid="ops-new" render={(j) => <JobRow j={j} />} />
          <OpsGroup title="Assigned quote jobs — no quote yet" items={ops.awaiting_quote} testid="ops-quote" render={(j) => <JobRow j={j} />} />
          <OpsGroup title="New partner applications (7 days)" items={ops.new_partners} testid="ops-partners" render={(u) => (
            <div className="jobcard" data-testid={`ops-partner-${u.id}`} style={{ marginBottom: 12 }}>
              <div className="head"><div><div className="od-eyebrow">{tradeLabel(u)}</div><h3 style={{ margin: 0, fontWeight: 500 }}>{u.name}</h3></div><span className="status-badge quote">New partner</span></div>
              <p style={{ color: "var(--taupe)", marginTop: 6, fontSize: ".85rem" }}>{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
            </div>
          )} />
          <OpsGroup title="Completed — balance unpaid" items={ops.unpaid_balances} testid="ops-unpaid" render={(j) => <JobRow j={j} note={j.price ? `£${(j.price * 0.75).toFixed(0)} outstanding` : ""} />} />
        </div>
      )}

      {/* ALL JOBS */}
      {tab === "jobs" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="admin-jobs-table">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".9rem" }}>
            <thead><tr style={{ textAlign: "left", background: "var(--cream-deep)" }}>
              {["Title", "Customer", "Category", "Status", "Assignee", ""].map((h, i) => <th key={i} style={{ padding: "14px 18px" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} style={{ borderTop: "1px solid var(--hairline)" }}>
                  <td style={{ padding: "14px 18px" }}><Link to={`/jobs/${j.id}`} data-testid={`admin-job-${j.id}`} className="od-link">{j.title}</Link></td>
                  <td style={{ padding: "14px 18px" }}>{j.customer_name}</td>
                  <td style={{ padding: "14px 18px" }}>{j.category}</td>
                  <td style={{ padding: "14px 18px" }}><span className={`status-badge ${STATUS_CLS[j.status] || "requested"}`}>{j.status.replace("_", " ")}</span></td>
                  <td style={{ padding: "14px 18px", color: "var(--taupe)" }}>{j.assignee_name || "—"}</td>
                  <td style={{ padding: "14px 18px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {["completed", "cancelled"].includes(j.status) && (
                      <button data-testid={`admin-reopen-job-${j.id}`} onClick={() => reopenJob(j)} title="Reopen job" style={{ color: "#3F6B4E", border: "1px solid #CBE0D2", borderRadius: 999, padding: "4px 12px", fontSize: ".8rem", background: "transparent", cursor: "pointer", marginRight: 8 }}>Reopen</button>
                    )}
                    <button data-testid={`admin-delete-job-${j.id}`} onClick={() => deleteJob(j)} title="Delete job" style={{ color: "#B04A4A", border: "1px solid #E9CFCF", borderRadius: 999, padding: "4px 12px", fontSize: ".8rem", background: "transparent", cursor: "pointer" }}>Delete</button>
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && <tr><td colSpan="6" style={{ padding: 40, textAlign: "center", color: "var(--taupe)" }}>No jobs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* PEOPLE */}
      {tab === "users" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="admin-users-table">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".9rem" }}>
            <thead><tr style={{ textAlign: "left", background: "var(--cream-deep)" }}>
              {["Name", "Email", "Role", "Specialism"].map((h) => <th key={h} style={{ padding: "14px 18px" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: "1px solid var(--hairline)" }}>
                  <td style={{ padding: "14px 18px", fontWeight: 500 }}>{u.name}</td>
                  <td style={{ padding: "14px 18px", color: "var(--taupe)" }}>{u.email}</td>
                  <td style={{ padding: "14px 18px", textTransform: "capitalize" }}>{u.role}</td>
                  <td style={{ padding: "14px 18px", color: "var(--taupe)" }}>{tradeLabel(u) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VETTING — partner document review */}
      {tab === "vetting" && (
        <div data-testid="admin-vetting">
          {(() => {
            const STATE_BADGE = {
              under_review: ["quote", "Awaiting review"], rejected: ["requested", "Needs fix"],
              verified: ["done", "Verified"], incomplete: ["requested", "Incomplete"], exempt: ["matched", "Established"],
            };
            const DOC_BADGE = { verified: ["done", "Approved"], submitted: ["quote", "Awaiting"], rejected: ["requested", "Rejected"], missing: ["requested", "Missing"] };
            const ordered = partners;
            if (ordered.length === 0) return <div className="card" style={{ textAlign: "center", color: "var(--taupe)", padding: 40 }}>No partners yet.</div>;
            return ordered.map((p) => {
              const sb = STATE_BADGE[p.state] || ["requested", p.state];
              return (
                <div className="card" key={p.id} data-testid={`vet-partner-${p.id}`} style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <div className="od-eyebrow" style={{ marginBottom: 4 }}>{tradeLabel(p) || "Partner"}</div>
                      <h3 style={{ margin: 0, fontWeight: 500 }}>{p.name}</h3>
                      <p style={{ color: "var(--taupe)", fontSize: ".85rem", margin: "4px 0 0" }}>{p.email}{p.phone ? ` · ${p.phone}` : ""}</p>
                    </div>
                    <span className={`status-badge ${sb[0]}`} data-testid={`vet-state-${p.id}`}>{sb[1]}</span>
                  </div>
                  {p.exempt ? (
                    <p style={{ color: "var(--taupe)", fontSize: ".85rem", marginTop: 12 }}>Established partner — documents optional. Any uploaded files can still be reviewed below.</p>
                  ) : null}
                  <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                    {p.items.map((it) => {
                      const db_ = DOC_BADGE[it.status] || ["requested", it.status];
                      return (
                        <div key={it.doc_type} data-testid={`vet-doc-${p.id}-${it.doc_type}`} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderTop: "1px solid var(--hairline)", paddingTop: 10 }}>
                          <span style={{ minWidth: 200, fontWeight: 500, fontSize: ".9rem" }}>{it.label}</span>
                          <span className={`status-badge ${db_[0]}`}>{db_[1]}</span>
                          {it.document && (
                            <a href={`${API_BASE}/partner/documents/${it.document.id}/download`} target="_blank" rel="noreferrer" className="od-link" data-testid={`vet-view-${p.id}-${it.doc_type}`} style={{ fontSize: ".85rem" }}>View file</a>
                          )}
                          {it.status === "rejected" && it.review_note && <span style={{ color: "#B04A4A", fontSize: ".8rem" }}>⚠ {it.review_note}</span>}
                          {it.document && it.status !== "verified" && (
                            <button className="btn rose sm" data-testid={`vet-approve-${p.id}-${it.doc_type}`} onClick={() => reviewDoc(it.document.id, "approve")}>Approve</button>
                          )}
                          {it.document && it.status !== "rejected" && (
                            <button className="btn secondary sm" data-testid={`vet-reject-${p.id}-${it.doc_type}`} style={{ color: "#B04A4A", borderColor: "#B04A4A" }} onClick={() => reviewDoc(it.document.id, "reject")}>Reject</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* RETENTION — rebooking nudges */}
      {tab === "retention" && (
        <div data-testid="admin-retention">
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="od-eyebrow" style={{ marginBottom: 6 }}>Seasonal check-up nudges</div>
            <h3 style={{ margin: "0 0 8px", fontWeight: 500 }}>Bring past customers back</h3>
            <p style={{ color: "var(--taupe)", margin: 0, maxWidth: 620 }}>
              Customers automatically receive a gentle, tailored check-up email {nudge ? nudge.delay_days : 90} days after a completed job. You can also send any that are due right now.
            </p>
          </div>
          <div className="ogrid cols-3" style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 20 }}>
            <div className="statcard" data-testid="nudge-due"><b style={{ fontSize: "1.7rem" }}>{nudge ? nudge.due_now : "—"}</b><span>Due now</span></div>
            <div className="statcard" data-testid="nudge-sent"><b style={{ fontSize: "1.7rem" }}>{nudge ? nudge.already_sent : "—"}</b><span>Nudges sent</span></div>
            <div className="statcard" data-testid="nudge-completed"><b style={{ fontSize: "1.7rem" }}>{nudge ? nudge.total_completed : "—"}</b><span>Completed jobs</span></div>
          </div>
          <button className="btn rose" data-testid="run-nudges-btn" onClick={runNudges} disabled={runningNudge || (nudge && nudge.due_now === 0)}>
            {runningNudge ? "Sending…" : nudge && nudge.due_now ? `Send ${nudge.due_now} due ${nudge.due_now === 1 ? "nudge" : "nudges"} now` : "Nothing due right now"}
          </button>
        </div>
      )}

      {/* EMAIL — compose & broadcast */}
      {tab === "email" && <EmailComposer users={users} />}
    </section>
  );
}

function OpsGroup({ title, items, render, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 26 }} data-testid={testid}>
      <div className="od-eyebrow" style={{ marginBottom: 12 }}>{title} ({items.length})</div>
      {items.map((it) => <React.Fragment key={it.id}>{render(it)}</React.Fragment>)}
    </div>
  );
}

function JobRow({ j, note }) {
  return (
    <Link to={`/jobs/${j.id}`} className="jobcard" data-testid={`ops-job-${j.id}`} style={{ display: "block", marginBottom: 12 }}>
      <div className="head">
        <div><div className="od-eyebrow">{j.category} · {j.urgency}</div><h3 style={{ margin: 0, fontWeight: 500 }}>{j.title}</h3></div>
        <span className={`status-badge ${STATUS_CLS[j.status] || "requested"}`}>{j.status.replace("_", " ")}</span>
      </div>
      <p style={{ color: "var(--taupe)", marginTop: 6, fontSize: ".85rem" }}>{j.customer_name} · {j.address || "no address"}{note ? ` · ${note}` : ""}</p>
    </Link>
  );
}
